import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import { useMemo, useState } from 'react';
import {
  useAddWorldElement,
  useBookWorlds,
  useCreateWorld,
  WORLD_ELEMENT_TYPES,
  type WorldDto,
} from '../../api/queries/worlds';

interface Props {
  bookId: string;
}

/**
 * World Builder v1 sidebar panel. Lists worlds associated with the book, groups each
 * world's elements by element_type, and provides Create-World + Add-Element dialogs.
 * Full Creativity Engine UX (standalone page, drag layout, relationships) lands in
 * Phase 3.
 */
export function WorldPanel({ bookId }: Props) {
  const { data: worlds = [] } = useBookWorlds(bookId);
  const [createOpen, setCreateOpen] = useState(false);
  const [addElementWorld, setAddElementWorld] = useState<WorldDto | null>(null);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Worlds
        </Typography>
        <Button size="small" onClick={() => setCreateOpen(true)}>
          + World
        </Button>
      </Stack>

      {worlds.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No worlds associated with this book. Create one to start building the
          setting.
        </Typography>
      )}

      {worlds.map((world) => (
        <Accordion key={world.id} disableGutters>
          <AccordionSummary>
            <Typography variant="body2">{world.name}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {world.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1 }}
              >
                {world.description}
              </Typography>
            )}
            <WorldElementsList world={world} />
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={() => setAddElementWorld(world)}
            >
              + Element
            </Button>
          </AccordionDetails>
        </Accordion>
      ))}

      <CreateWorldDialog
        bookId={bookId}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <AddElementDialog
        bookId={bookId}
        world={addElementWorld}
        onClose={() => setAddElementWorld(null)}
      />
    </Paper>
  );
}

function WorldElementsList({ world }: { world: WorldDto }) {
  const grouped = useMemo(() => {
    const groups: Record<string, typeof world.elements> = {};
    for (const element of world.elements) {
      (groups[element.elementType] ??= []).push(element);
    }
    return groups;
  }, [world.elements]);

  if (world.elements.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No elements yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {Object.entries(grouped).map(([type, elements]) => (
        <div key={type}>
          <Typography variant="caption" color="text.secondary">
            {type.replace(/_/g, ' ')}
          </Typography>
          {elements.map((element) => (
            <Typography key={element.id} variant="body2" sx={{ ml: 1 }}>
              · {element.name}
            </Typography>
          ))}
        </div>
      ))}
    </Stack>
  );
}

function CreateWorldDialog({
  bookId,
  open,
  onClose,
}: {
  bookId: string;
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateWorld(bookId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) return;
    await create.mutateAsync({
      name: name.trim(),
      description: description.trim() || undefined,
    });
    setName('');
    setDescription('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New world</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || create.isPending}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddElementDialog({
  bookId,
  world,
  onClose,
}: {
  bookId: string;
  world: WorldDto | null;
  onClose: () => void;
}) {
  const add = useAddWorldElement(bookId);
  const [name, setName] = useState('');
  const [type, setType] = useState('culture');
  const [description, setDescription] = useState('');

  const handleSubmit = async () => {
    if (!world || !name.trim()) return;
    await add.mutateAsync({
      worldId: world.id,
      name: name.trim(),
      elementType: type,
      description: description.trim() || undefined,
    });
    setName('');
    setDescription('');
    onClose();
  };

  return (
    <Dialog open={Boolean(world)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add element to {world?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <FilterableSelect
            options={WORLD_ELEMENT_TYPES.map((t) => ({
              value: t.value,
              label: t.label,
            }))}
            value={type}
            onChange={(newValue) => setType(newValue)}
            label="Type"
            allowEmpty={false}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || add.isPending}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
